import { useEffect, useId, useRef, useState } from "react";

import { useLanguage } from "context/languageContext";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { detectTextLang, translations } from "utils/i18n";

export function NameDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  initialValue = "",
  submitLabel,
  cancelLabel,
  onSubmit,
}) {
  const { language } = useLanguage();
  const tr = translations[language].nameDialog;
  const resolvedSubmitLabel = submitLabel ?? tr.save;
  const resolvedCancelLabel = cancelLabel ?? tr.cancel;
  const inputId = useId();
  const inputRef = useRef(null);
  const [value, setValue] = useState(initialValue);

  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) setValue(initialValue);
  }

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const trimmed = value.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (trimmed === "") return;
            onSubmit(trimmed);
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>

          <div className="grid gap-2 py-5">
            <Label htmlFor={inputId}>{label}</Label>
            <Input
              ref={inputRef}
              id={inputId}
              value={value}
              lang={detectTextLang(value)}
              autoFocus
              onChange={(event) => setValue(event.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {resolvedCancelLabel}
            </Button>
            <Button type="submit" disabled={trimmed === ""}>
              {resolvedSubmitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
